/**
 * CallFeedItem (KAN-929): звонок менеджера отдельным блоком в ленте переписки
 * (кабинет: ContactConversationPanel; приложение /m: MobileChatFeed).
 *
 * Строка: направление, менеджер, итог, длительность (м:сс), время. Под ней
 * краткое содержание, кнопка "Слушать" (запись грузится через API с токеном
 * только после нажатия) и свёрнутая "Расшифровка" по репликам. Неотвеченный
 * звонок без записи и текста остаётся одной строкой без кнопок.
 * Звонок бота (bot: true): подпись "Звонок бота", итог и только "Слушать"
 * (его реплики уже идут в ленте сообщениями). recording null (записи нет или
 * она недоступна): кнопки "Слушать" нет.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Phone, PhoneIncoming, PhoneMissed, Play } from 'lucide-react';
import { AudioPlayer, formatAudioTime } from '@/components/media/AudioPlayer';
import { useApiFileSrc } from '@/hooks/useApiFileSrc';
import { cn } from '@/lib/utils';
import type { ClientCall, ClientCallRecording, ClientCallTranscriptLine } from '@/types/clientCall';

const OTHER_OUTCOME_LABELS: Record<string, string> = {
  to_bot: 'Переведён на бота',
};
const OTHER_FALLBACK_LABEL = 'Звонок не состоялся';

const RESULT_LABELS: Record<Exclude<ClientCall['result'], 'other'>, string> = {
  talked: 'Разговор',
  client_no_answer: 'Клиент не ответил',
  manager_no_answer: 'Менеджер не ответил',
  in_progress: 'Идёт звонок',
};

const SPEAKER_LABELS: Record<ClientCallTranscriptLine['speaker'], string> = {
  manager: 'Менеджер',
  client: 'Клиент',
};

function callTitle(call: ClientCall): string {
  if (call.bot) return 'Звонок бота';
  return call.direction === 'inbound' ? 'Входящий звонок' : 'Исходящий звонок';
}

function callResultLabel(call: Pick<ClientCall, 'result' | 'outcome'>): string {
  if (call.result === 'other') return OTHER_OUTCOME_LABELS[call.outcome ?? ''] ?? OTHER_FALLBACK_LABEL;
  return RESULT_LABELS[call.result];
}

const BUTTON =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-xs font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100';

function CallRecording({ recording }: { recording: ClientCallRecording }) {
  const [open, setOpen] = useState(false);
  const src = useApiFileSrc(open ? recording.url : '', recording.id);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={BUTTON}>
        <Play className="h-3.5 w-3.5" aria-hidden="true" />
        Слушать
      </button>
    );
  }
  if (!src) return <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Загрузка записи" />;
  return <AudioPlayer src={src} durationSeconds={recording.duration_seconds} />;
}

function CallTranscript({ lines }: { lines: ClientCallTranscriptLine[] }) {
  const [open, setOpen] = useState(false);
  const Icon = open ? ChevronUp : ChevronDown;
  return (
    <div className="space-y-1.5">
      <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)} className={BUTTON}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        Расшифровка
      </button>
      {open && (
        <ul className="space-y-1 rounded-lg bg-white p-2 text-sm">
          {lines.map((line, index) => (
            <li key={`${index}-${line.start ?? ''}`} data-testid="call-transcript-line" className="break-words">
              <span className={cn('font-medium', line.speaker === 'manager' ? 'text-emerald-700' : 'text-blue-700')}>
                {SPEAKER_LABELS[line.speaker]}:
              </span>{' '}
              <span className="text-gray-800">{line.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface CallFeedItemProps {
  call: ClientCall;
  /** Время звонка в формате ленты (у кабинета и приложения свой). */
  time?: string;
}

export function CallFeedItem({ call, time }: CallFeedItemProps): JSX.Element {
  const talked = call.result === 'talked';
  const missed = call.result === 'client_no_answer' || call.result === 'manager_no_answer';
  const Icon = missed ? PhoneMissed : call.direction === 'inbound' ? PhoneIncoming : Phone;
  const duration = (call.duration_seconds ?? 0) > 0 ? formatAudioTime(call.duration_seconds) : null;
  // A bot call carries only its recording: its lines are already messages.
  const summary = call.bot ? '' : call.summary;
  const transcript = call.bot ? [] : call.transcript;
  const hasDetails = !!summary || !!call.recording || transcript.length > 0;

  return (
    <div className="flex justify-center" data-testid="call-feed-item">
      <div
        className={cn(
          'w-full max-w-[90%] rounded-lg border px-3 py-2 text-sm',
          talked ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'
        )}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-600">
          <Icon className={cn('h-3.5 w-3.5 shrink-0', missed ? 'text-red-500' : 'text-emerald-600')} aria-hidden="true" />
          <span className="font-medium text-gray-900">
            {callTitle(call)}
          </span>
          {!call.bot && call.manager_name && <span>{call.manager_name}</span>}
          <span className={cn(missed ? 'text-red-600' : talked ? 'text-emerald-700' : 'text-gray-700')}>
            {callResultLabel(call)}
          </span>
          {duration && <span>{duration}</span>}
          {time && <span className="text-gray-400">{time}</span>}
        </div>
        {hasDetails && (
          <div className="mt-2 space-y-2">
            {summary && <p className="whitespace-pre-wrap break-words text-gray-800">{summary}</p>}
            {call.recording && <CallRecording recording={call.recording} />}
            {transcript.length > 0 && <CallTranscript lines={transcript} />}
          </div>
        )}
      </div>
    </div>
  );
}
